/**
 * Edit operations over an immutable edit state. Each op returns a *new* EditState; the
 * caller (undo/redo) keeps the history. The state is a list of stints, each an ordered list
 * of lengths — never index ranges (see IMPLEMENTATION_PLAN.md: an id/list model deletes the
 * index-remapping bug class). Serialisation details (message_index, first_length_index) live
 * only in the materialiser.
 */
import { FitError } from '../codec/errors'
import type { DataRecord, FitFile } from '../codec/types'
import { R } from './recompute'
import type { Activity } from './model'

export const UNSET_STROKE = 255
export const SPLIT_MIN = 2
export const SPLIT_MAX = 10

export interface EditLength {
  readonly active: boolean
  readonly durationMs: number
  /** Active-only stroke count; 0 for idle. */
  readonly strokes: number
  readonly startTime: number
  /** swim_stroke enum, or UNSET_STROKE. */
  readonly swimStroke: number
  /** Original length record to clone untouched fields (calories, HR, timestamp…) from. */
  readonly template: DataRecord
}

export interface EditStint {
  readonly lapTemplate: DataRecord
  readonly lengths: readonly EditLength[]
}

export interface EditState {
  readonly originalFile: FitFile
  readonly poolLengthCm: number
  readonly stints: readonly EditStint[]
  /**
   * Whether any structural length edit (merge/split/pool) has happened. Drives dropping the
   * global-113 best-effort records. A set-stroke edit deliberately does NOT set this.
   */
  readonly lengthsTouched: boolean
}

export function buildEditState(activity: Activity): EditState {
  return {
    originalFile: activity.file,
    poolLengthCm: activity.poolLengthCm,
    lengthsTouched: false,
    stints: activity.stints.map((stint) => ({
      lapTemplate: stint.lapRecord,
      lengths: stint.lengths.map((l) => ({
        active: l.active,
        durationMs: l.durationMs,
        strokes: l.strokes,
        startTime: l.startTime,
        swimStroke: l.swimStroke,
        template: l.record,
      })),
    })),
  }
}

/** Flat view of all lengths in order, with the stint each belongs to. */
export function allLengths(state: EditState): { length: EditLength; stintIndex: number }[] {
  const out: { length: EditLength; stintIndex: number }[] = []
  state.stints.forEach((stint, stintIndex) => {
    for (const length of stint.lengths) out.push({ length, stintIndex })
  })
  return out
}

function replaceStint(state: EditState, stintIndex: number, lengths: EditLength[]): EditStint[] {
  return state.stints.map((s, i) => (i === stintIndex ? { ...s, lengths } : s))
}

function locate(state: EditState, ordinal: number): { stintIndex: number; localIndex: number } {
  let seen = 0
  for (let s = 0; s < state.stints.length; s++) {
    const n = state.stints[s]!.lengths.length
    if (ordinal < seen + n) return { stintIndex: s, localIndex: ordinal - seen }
    seen += n
  }
  throw new FitError(`length ordinal ${ordinal} out of range`)
}

/** Result of a precondition check: usable by the UI to disable a button and explain why. */
export interface Check {
  readonly ok: boolean
  readonly reason?: string
}
const OK: Check = { ok: true }
const no = (reason: string): Check => ({ ok: false, reason })

function tryLocate(
  state: EditState,
  ordinal: number,
): { stintIndex: number; localIndex: number } | null {
  try {
    return locate(state, ordinal)
  } catch {
    return null
  }
}

/**
 * The shortest part duration a split may produce, in ms: the max of a 2 s floor and the ceilings
 * that keep the derived uint16 `avg_speed` and uint8 `avg_swimming_cadence` in range for the
 * fastest resulting part. Below this a split would overflow those fields at encode time.
 */
export function minPartMs(poolLengthCm: number, strokesPerPart: number): number {
  const speedFloor = Math.ceil((poolLengthCm * 10000) / 65534)
  const cadenceFloor = strokesPerPart > 0 ? Math.ceil((strokesPerPart * 60000) / 254) : 0
  return Math.max(2000, speedFloor, cadenceFloor)
}

/** Whether a merge of these ordinals is legal, with a reason when not. */
export function canMerge(state: EditState, ordinals: readonly number[]): Check {
  if (ordinals.length < 2) return no('Select at least two lengths to merge')
  const sorted = [...ordinals].sort((a, b) => a - b)
  const located = sorted.map((o) => tryLocate(state, o))
  if (located.some((l) => l === null)) return no('Selection is out of range')
  const stintIndex = located[0]!.stintIndex
  for (let i = 0; i < located.length; i++) {
    if (located[i]!.stintIndex !== stintIndex) return no('Lengths span more than one stint')
    if (i > 0 && located[i]!.localIndex !== located[i - 1]!.localIndex + 1) {
      return no('Lengths are not adjacent')
    }
  }
  const stint = state.stints[stintIndex]!
  if (!located.every((l) => stint.lengths[l!.localIndex]!.active)) return no('Cannot merge a rest')
  return OK
}

/** Whether splitting this length into `parts` is legal, with a reason when not. */
export function canSplit(state: EditState, ordinal: number, parts: number): Check {
  if (parts < SPLIT_MIN || parts > SPLIT_MAX)
    return no(`Split must be ${SPLIT_MIN}–${SPLIT_MAX} parts`)
  const located = tryLocate(state, ordinal)
  if (!located) return no('Selection is out of range')
  const src = state.stints[located.stintIndex]!.lengths[located.localIndex]!
  if (!src.active) return no('Cannot split a rest')
  const base = Math.floor(src.durationMs / parts)
  if (base < minPartMs(state.poolLengthCm, Math.ceil(src.strokes / parts))) {
    return no('Resulting lengths would be too short')
  }
  return OK
}

/**
 * Merge a contiguous run of active lengths (given by global ordinals) within one stint into a
 * single length. Duration, strokes and calories add; start_time is the first length's
 * (preserved); stroke is the common value or the first.
 */
export function mergeLengths(state: EditState, ordinals: readonly number[]): EditState {
  const check = canMerge(state, ordinals)
  if (!check.ok) throw new FitError(check.reason!)
  const sorted = [...ordinals].sort((a, b) => a - b)
  const located = sorted.map((o) => locate(state, o))
  const stintIndex = located[0]!.stintIndex
  const stint = state.stints[stintIndex]!
  const members = located.map((l) => stint.lengths[l.localIndex]!)

  const strokes = members.reduce((n, m) => n + m.strokes, 0)
  const firstStroke = members[0]!.swimStroke
  const merged: EditLength = {
    active: true,
    durationMs: members.reduce((n, m) => n + m.durationMs, 0),
    strokes,
    startTime: members[0]!.startTime,
    swimStroke: members.every((m) => m.swimStroke === firstStroke) ? firstStroke : firstStroke,
    template: members[0]!.template,
  }

  const start = located[0]!.localIndex
  const next = [...stint.lengths]
  next.splice(start, members.length, merged)
  return { ...state, stints: replaceStint(state, stintIndex, next), lengthsTouched: true }
}

/**
 * Split one active length into N equal parts. Duration and strokes are distributed with exact
 * remainder so Σ is conserved to the millisecond and stroke; synthesised start_times step
 * forward (half-up) from the source, which is preserved on the first part.
 */
export function splitLength(state: EditState, ordinal: number, parts: number): EditState {
  const check = canSplit(state, ordinal, parts)
  if (!check.ok) throw new FitError(check.reason!)
  const { stintIndex, localIndex } = locate(state, ordinal)
  const src = state.stints[stintIndex]!.lengths[localIndex]!

  const base = Math.floor(src.durationMs / parts)
  const rem = src.durationMs - base * parts
  const sBase = Math.floor(src.strokes / parts)
  const sRem = src.strokes - sBase * parts

  const pieces: EditLength[] = []
  let startTime = src.startTime
  for (let k = 0; k < parts; k++) {
    const durationMs = base + (k < rem ? 1 : 0)
    pieces.push({
      active: true,
      durationMs,
      strokes: sBase + (k < sRem ? 1 : 0),
      startTime,
      swimStroke: src.swimStroke,
      template: src.template,
    })
    startTime = startTime + R(durationMs / 1000)
  }

  const next = [...state.stints[stintIndex]!.lengths]
  next.splice(localIndex, 1, ...pieces)
  return { ...state, stints: replaceStint(state, stintIndex, next), lengthsTouched: true }
}

/** Set (or clear, with UNSET_STROKE) the swim stroke on active lengths matching `ordinals`. */
export function setStroke(
  state: EditState,
  ordinals: readonly number[],
  swimStroke: number,
): EditState {
  const targets = new Set(ordinals)
  let global = 0
  const stints = state.stints.map((stint) => ({
    ...stint,
    lengths: stint.lengths.map((length) => {
      const ord = global++
      if (!length.active || !targets.has(ord) || length.swimStroke === swimStroke) return length
      return { ...length, swimStroke }
    }),
  }))
  return { ...state, stints } // note: does NOT set lengthsTouched (keeps global-113 records)
}

/** All active-length ordinals — convenience for "set stroke on the whole activity". */
export function activeOrdinals(state: EditState): number[] {
  return allLengths(state)
    .map((x, i) => ({ active: x.length.active, i }))
    .filter((x) => x.active)
    .map((x) => x.i)
}

/** Change the pool length (centimetres) for the whole activity. */
export function setPoolLength(state: EditState, poolLengthCm: number): EditState {
  if (poolLengthCm <= 0 || poolLengthCm > 65534) throw new FitError('pool length out of range')
  return { ...state, poolLengthCm, lengthsTouched: true }
}
